import React from "react";
import { Rnd } from "react-rnd";
import QRGenerator from "../../../components/QRGenerator";
import "./IDCardDesignerPage.css";
const webUrl = process.env.REACT_APP_WEB_URL!;

export interface FieldPosition {
    id?: number;
    field: string;
    x: number;
    y: number;
    width: number;
    height: number;
    styles: {
        fontSize?: number;
        fontFamily?: string;
        color?: string;
        backgroundColor?: string;
        borderColor?: string;
        borderWidth?: number;
        borderRadius?: number;
        fontWeight?: "normal" | "bold";
        fontStyle?: "normal" | "italic";
        textAlign?: "left" | "center" | "right";
        zIndex?: number;
        hasBorder?: boolean; // ✅ NEW
    };
    enabled: boolean;
}

export interface TestPersonData {
    firstName: string;
    lastName: string;
    function: string;
    title: string;     // ✅ NEW
    company: string;   // ✅ NEW
    zones: { number: number; name: string }[];
    personId: number;
    eventId: number;
    profileUrl?: string;
    shifts?: { date: string; }[];
}

interface IDCardFormProps {
    testData: TestPersonData;
    fields: FieldPosition[];
    setFields: (fields: FieldPosition[]) => void;
    backgroundPreview: string | null;
    selectedElementIndex: number | null;
    setSelectedElementIndex: (index: number | null) => void;
}

const IDCardForm: React.FC<IDCardFormProps> = ({
    testData,
    fields,
    setFields,
    backgroundPreview,
    selectedElementIndex,
    setSelectedElementIndex,
}) => {

    const updateField = (index: number, newProps: Partial<FieldPosition>) => {
        const updated = [...fields];
        updated[index] = { ...updated[index], ...newProps };
        setFields(updated);
    };

    return (
        <div
            className="id-card-preview"
            style={{
                width: "100%",
                height: "100%",
                position: "relative",
                backgroundImage: backgroundPreview ? `url(${backgroundPreview})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
            }}
        >
          <div className="grid-overlay" />
            {fields.map((f, i) => {
                if (!f.enabled) return null;
                const style = f.styles || {};
                let content: React.ReactNode = null;

                switch (f.field) {
                    case "profile":
                        content = testData.profileUrl ? (
                            <img src={testData.profileUrl} alt="Profile"
                                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6 }} />
                        ) : <div className="no-image">No Image</div>;
                        break;
                    case "name":
                        content = (
                            <span
                                style={{
                                    ...style,
                                    fontWeight: style.fontWeight || "normal",
                                    fontStyle: style.fontStyle || "normal",
                                    textAlign: style.textAlign || "left",
                                    display: "block",
                                    width: "100%",
                                }}
                            >
                                {testData.firstName} {testData.lastName}
                            </span>
                        );
                        break;
                    case "function":
                        content = (
                            <span
                                style={{
                                    ...style,
                                    fontWeight: style.fontWeight || "normal",
                                    fontStyle: style.fontStyle || "normal",
                                    textAlign: style.textAlign || "left",
                                    display: "block",
                                    width: "100%",
                                }}
                            >
                                {testData.function}
                            </span>
                        );
                        break;
                    case "title":
                        content = (
                            <span
                                style={{
                                    ...style,
                                    fontWeight: style.fontWeight || "normal",
                                    fontStyle: style.fontStyle || "normal",
                                    textAlign: style.textAlign || "left",
                                    display: "block",
                                    width: "100%",
                                }}
                            >
                                {testData.title}
                            </span>
                        );
                        break;

                    case "company":
                        content = (
                            <span
                                style={{
                                    ...style,
                                    fontWeight: style.fontWeight || "normal",
                                    fontStyle: style.fontStyle || "normal",
                                    textAlign: style.textAlign || "left",
                                    display: "block",
                                    width: "100%",
                                }}
                            >
                                {testData.company}
                            </span>
                        );
                        break;
                    case "zonesList":
                        content = (
                            <ul style={{
                                margin: 0, padding: 6, listStyle: "none", fontSize: style.fontSize, fontFamily: style.fontFamily, fontWeight: style.fontWeight || "normal",
                                fontStyle: style.fontStyle || "normal", textAlign: style.textAlign || "left",
                                display: "block",
                                width: "100%", color: style.color }}>
                                {testData.zones.map(z => <li key={z.number}>{z.number} - {z.name}</li>)}
                            </ul>
                        );
                        break;
                    case "zoneCircles":
                        content = (
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
                                {testData.zones.map(z => (
                                    <div key={z.number} style={{
                                        width: 30, height: 30, borderRadius: "50%",
                                        border: "2px solid #000",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: style.fontSize || 12,
                                        fontFamily: style.fontFamily || "Arial",
                                        fontWeight: style.fontWeight || "bold",
                                        fontStyle: style.fontStyle || "normal",
                                        color: style.color || "#000",
                                        background: style.backgroundColor || "transparent",
                                    }}>{z.number}</div>
                                ))}
                            </div>
                        );
                        break;
                    case "qr":
                        content = <QRGenerator value={`${webUrl}/epos/${testData.eventId}/${testData.personId}`} size={f.width} />;
                        break;
                    case "copy":
                        content = (
                            <span
                                style={{
                                    ...style,
                                    fontWeight: style.fontWeight || "normal",
                                    fontStyle: style.fontStyle || "normal",
                                    textAlign: style.textAlign || "left",
                                    display: "block",
                                    width: "100%",
                                }}
                            >
                                Kopi
                            </span>
                        );
                        break;
                    case "shifts":
                        content = (
                            <div
                                style={{
                                    ...style,
                                    fontWeight: style.fontWeight || "normal",
                                    fontStyle: style.fontStyle || "normal",
                                    textAlign: style.textAlign || "left",
                                    width: "100%",
                                    fontSize: style.fontSize || 10,
                                }}
                            >
                                {testData.shifts && testData.shifts.length < 3 && (
                                    <>
                                        <div style={{ marginBottom: 4 }}>
                                            Kun gyldigt disse dage:
                                        </div>

                                        {testData.shifts.map((s, idx) => (
                                            <div key={idx}>{s.date}</div>
                                        ))}
                                    </>
                                )}
                            </div>
                        );
                        break;
                    case "shape-rect":
                    case "shape-circle":
                        content = (
                            <div
                                className="shape-element"
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    background: style.backgroundColor || "transparent",
                                    border: style.hasBorder
                                        ? `${style.borderWidth || 1}px solid ${style.borderColor || "#000"}`
                                        : "none",
                                    borderRadius:
                                        f.field === "shape-circle"
                                            ? "50%"
                                            : style.borderRadius || 0,
                                }}
                            />
                        );
                        break;
                    default:
                        content = <span>{f.field}</span>;
                }

                const isShape =
                    f.field === "shape-rect" ||
                    f.field === "shape-circle";

                return (
                    <Rnd
                        key={i}
                        bounds="parent"
                        size={{ width: f.width, height: f.height }}
                        position={{ x: f.x, y: f.y }}
                        style={{
                            zIndex: isShape ? 1 : 2,
                        }}
                        onDragStop={(e, d) => updateField(i, { x: d.x, y: d.y })}
                        onResizeStop={(e, dir, ref, delta, pos) =>
                            updateField(i, {
                                width: ref.offsetWidth,
                                height: ref.offsetHeight,
                                x: pos.x,
                                y: pos.y,
                            })
                        }
                        enableResizing
                        dragGrid={[1, 1]}
                        resizeGrid={[1, 1]}
                    >
                        <div
                            onClick={(e) => { e.stopPropagation(); setSelectedElementIndex(i); }}
                            className={selectedElementIndex === i ? "selected-element" : "card-element"}
                            style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
                        >
                            {content}
                        </div>
                    </Rnd>
                );
            })}
        </div>
    );
};

export default IDCardForm;